import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Eye, CheckCircle, Search, Trash2, ChevronUp, ChevronDown, HelpCircle,
  CheckSquare, XCircle, Code, AlignLeft, FileText, Image, LayoutGrid, Type, Plus,
  Calculator, MessageSquare, ImageIcon, AlignCenter, AlignJustify, ListOrdered, List, Quote, Table,
  Minus, Hash, Paperclip, Undo, Redo, Copy, ArrowUp, ArrowDown,
  GripVertical, X, ChevronRight, Clock, AlertCircle, Check, Star, Edit3, MinusCircle,
  Settings2, EyeOff, Trash, MousePointer, Save, Upload, Code2, Lightbulb, Palette,
  Eraser, Pencil
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import ImageUploadModal from '../../components/ImageUploadModal';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import GraphicsQuillEditor from '../../components/GraphicsQuillEditor';
import QuillEditorWithImageResize from '../../components/QuillEditorWithImageResize';
import ObjectiveQuestionEditor from '../../components/admin/ObjectiveQuestionEditor';
import ObjectiveQuestionPreview from '../../components/admin/ObjectiveQuestionPreview';
import WYSIWYGQuestionPreview from '../../components/admin/WYSIWYGQuestionPreview';
import QuizStyleQuestionView from '../../components/admin/QuizStyleQuestionView';
import InsertQuestionModal from '../../components/admin/InsertQuestionModal';
import ScratchQuestionEditor from '../../components/admin/ScratchQuestionEditor';
import PythonQuestionEditor from '../../components/admin/PythonQuestionEditor';
import CppQuestionEditor from '../../components/admin/CppQuestionEditor';
import AnswerCard from '../../pages/quiz/components/AnswerCard';
import QuizAnswerMode from '../../pages/quiz/QuizAnswerMode';
import paperStorage from '../../utils/paperStorage';
import { supabase, getSupabaseUrl } from '../../supabase/client';
// Scratch types removed - scratch editor deprecated
type ScratchQuestionSubtype = 'algorithm' | 'operation' | 'creative';
type AlgorithmConfig = any;
type OperationConfig = any;
type CreativeConfig = any;

interface Question {
  id: string;
  type: string;
  subType?: 'scratch' | 'python' | 'cpp';
  questionTitle?: string; // 题目名称（非客观题专用）
  questionStem: string; // 题干内容
  content?: string; // 保留用于向后兼容
  placeholder?: string;
  required: boolean;
  inputType?: string;
  options: Option[];
  score: number;
  showAnswer?: boolean;
  answerAnalysis?: string;
  titleImages: string[];
  titleImageWidths?: number[];
  imagePosition?: 'above' | 'below';
  titleImageLayout?: string;
  optionLayout?: string;
  answerImages?: string[];
  answerImageWidths?: number[];
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  gradingMode?: 'manual' | 'ai';
  scoringPoints?: string;
  explanation?: string;
  answerText?: string;
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  graphicsAnswerItems?: GraphicsAnswerItem[];
  graphicsDifficulty?: 'easy' | 'medium' | 'hard';
  graphicsCategory?: string;
  graphicsContent?: string;
  scratchAnswerItems?: ScratchAnswerItem[];
  // Scratch三种题型配置
  scratchSubtype?: ScratchQuestionSubtype;
  scratchConfig?: AlgorithmConfig | OperationConfig | CreativeConfig;
  referenceSb3Url?: string;
  optionImageWidths?: Record<string, number>;
  // Python编辑器新增字段
  difficulty?: 'easy' | 'medium' | 'hard';
  knowledgePointIds?: string[];
  commonMistakes?: string;
  timeLimitMs?: number;
  memoryLimitMb?: number;
  testCases?: any[];
  structuredScoring?: {
    points: { id: string; name: string; score: number }[];
    commonMistakes: string;
  };
}

interface ScratchAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  aiReferenceImage?: string;
  aiReferenceImageWidth?: number;
  aiScoringPoints?: string;
}

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  // 旧字段保留兼容
  image?: string;        // 单张图片(向后兼容)
  imageWidth?: number;   // 单张图片宽度

  // 新字段:多张图片支持
  images?: string[];           // 图片URL数组
  imageWidths?: number[];      // 对应每张图片的宽度百分比数组
}

interface GraphicsAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  maxCount: number;
  aiReferenceImage?: string;
  aiScoringPoints?: string;
}

interface ImageUploadTarget {
  questionId: string;
  optionId?: string;
  existingImageIndex?: number;
}

interface ImageViewerState {
  isOpen: boolean;
  imageUrl: string;
  title: string;
  currentIndex: number;
  totalCount: number;
}

const questionTypes = [
  {
    category: '考试题型',
    items: [
      { icon: CheckSquare, label: '单选题', type: 'single_choice', placeholder: '请选择' },
      { icon: CheckCircle, label: '判断题', type: 'judge', placeholder: '请选择' },
      { icon: AlignLeft, label: '多选题', type: 'multi_choice', placeholder: '请选择' },
      { icon: Code2, label: 'Scratch编程题', type: 'scratch_coding', placeholder: '请输入代码', scratchSubtype: 'algorithm' as ScratchQuestionSubtype },
      { icon: Code, label: 'Python编程题', type: 'python_coding', placeholder: '请输入代码' },
      { icon: Code, label: 'C++编程题', type: 'cpp_coding', placeholder: '请输入代码' }
    ]
  }
];

// 获取题型标签
const getQuestionTypeLabel = (type: string) => {
  const typeMap: Record<string, string> = {
    'single_choice': '单选题',
    'multi_choice': '多选题',
    'judge': '判断题',
    'subjective': '主观题',
    'python_coding': 'Python编程题',
    'cpp_coding': 'C++编程题',
    'scratch_coding': 'Scratch编程题'
  };
  return typeMap[type] || '未知题型';
};

// 获取题型图标
const getQuestionTypeIcon = (type: string) => {
  const iconMap: Record<string, any> = {
    'single_choice': CheckSquare,
    'multi_choice': AlignLeft,
    'judge': CheckCircle,
    'subjective': AlignLeft,
    'python_coding': Code,
    'cpp_coding': Code,
    'scratch_coding': Code2
  };
  return iconMap[type] || HelpCircle;
};

interface PaperEditorProps {
  isTeacherMode?: boolean;
}

export default function PaperEditor({ isTeacherMode: propIsTeacherMode }: PaperEditorProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editPaperId = searchParams.get('edit');
  const pendingPaperId = searchParams.get('pending');
  const targetQuestionId = searchParams.get('question');

  // 优先使用传入的 prop，否则从 URL 判断
  const actualIsTeacherMode = propIsTeacherMode ?? location.pathname.startsWith('/teacher');
  const papersTable = actualIsTeacherMode ? 'teacher_papers' : 'papers';
  const questionsTable = actualIsTeacherMode ? 'teacher_questions' : 'questions';
  const pendingPapersTable = actualIsTeacherMode ? 'teacher_pending_papers' : 'pending_papers';

  const [isPendingPaper, setIsPendingPaper] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [remark, setRemark] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('type');
  const [showScoreDropdown, setShowScoreDropdown] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUploadTarget, setImageUploadTarget] = useState<ImageUploadTarget | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [imageWidth, setImageWidth] = useState(100);
  const [imageViewer, setImageViewer] = useState<ImageViewerState>({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  const [showImageUploadDialog, setShowImageUploadDialog] = useState(false);
  const [imageUploadDialogConfig, setImageUploadDialogConfig] = useState<{
    title: string;
    target: 'title' | 'answer' | 'scoringPoints' | 'referenceAnswer';
    questionId: string;
  } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showWordImportModal, setShowWordImportModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [saveConfirmAction, setSaveConfirmAction] = useState<'draft' | 'saved' | 'published'>('draft');
  const [isSaving, setIsSaving] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unmountedRef = useRef(false);
  // 题目名称自动保存防抖定时器
  const titleSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draggedQuestion, setDraggedQuestion] = useState<Question | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Quiz风格预览模式相关状态
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasWidthChanges, setHasWidthChanges] = useState(false);
  const [isSavingWidths, setIsSavingWidths] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  // 插入题目弹窗相关状态
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertTargetQuestionId, setInsertTargetQuestionId] = useState<string | null>(null);
  // 复制题目确认弹窗相关状态
  const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
  const [copyTargetQuestionId, setCopyTargetQuestionId] = useState<string | null>(null);

  // 插入题目确认处理
  const handleInsertConfirm = (position: 'before' | 'after') => {
    if (!insertTargetQuestionId) return;
    const targetQuestion = questions.find(q => q.id === insertTargetQuestionId);
    if (!targetQuestion) return;

    const questionType = targetQuestion.type;
    const newQuestion: Question = {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: questionType,
      questionTitle: '',
      title: '',
      placeholder: questionType === 'scratch_coding' ? '请输入代码' : '请选择',
      required: true,
      options: [],
      score: targetQuestion.score,
      showAnswer: false,
      titleImages: [],
      imagePosition: 'above',
      titleImageLayout: 'default',
      optionLayout: 'list',
    };

    // 根据题型设置默认选项
    if (questionType === 'single_choice') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false },
      ];
    } else if (questionType === 'judge') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
      ];
    } else if (questionType === 'multi_choice') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false },
      ];
    } else if (questionType === 'subjective') {
      newQuestion.placeholder = '请输入答案';
    } else if (questionType === 'python_coding') {
      newQuestion.placeholder = '请输入Python代码';
    } else if (questionType === 'cpp_coding') {
      newQuestion.placeholder = '请输入C++代码';
    } else if (questionType === 'scratch_coding') {
      newQuestion.placeholder = '请输入代码';
      newQuestion.scratchSubtype = 'algorithm';
    }

    // 根据位置参数决定插入位置
    const targetIndex = questions.findIndex(q => q.id === insertTargetQuestionId);
    const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;

    const newQuestions = [
      ...questions.slice(0, insertIndex),
      newQuestion,
      ...questions.slice(insertIndex)
    ];

    setQuestions(newQuestions);
    setCurrentIndex(insertIndex);
    setIsEditing(true);
    setShowInsertModal(false);
    setInsertTargetQuestionId(null);
  };

  // 复制题目确认处理
  const handleCopyConfirm = () => {
    if (!copyTargetQuestionId) return;
    const targetIndex = questions.findIndex(q => q.id === copyTargetQuestionId);
    if (targetIndex === -1) return;
    const targetQuestion = questions[targetIndex];
    const copiedQuestion: Question = JSON.parse(JSON.stringify(targetQuestion));
    copiedQuestion.id = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newQuestions = [...questions.slice(0, targetIndex + 1), copiedQuestion, ...questions.slice(targetIndex + 1)];
    setQuestions(newQuestions);
    setCurrentIndex(targetIndex + 1);
    setShowCopyConfirmModal(false);
    setCopyTargetQuestionId(null);
  };

  const [paperSettings, setPaperSettings] = useState({
    year: '',
    month: '',
    organization: '',
    source: '',
    nature: '付费',
    categoryType: '',
    level_text: '',
    paperType: '',
    duration: '60'
  });

  const generateAutoTitle = useCallback(() => {
    const { year, month, organization, categoryType, level_text, source } = paperSettings;
    if (!year || !organization || !categoryType || !level_text || !source) {
      return '';
    }
    const monthPart = month && month !== '0' ? `${month}月` : '';
    const categoryName = categoryStore.getCategoryNameByCode(categoryType) || categoryType;
    return `${year}年${monthPart}${organization}${categoryName}${level_text}${source}`;
  }, [paperSettings]);

  // 组件卸载时标记，防止异步回调（setTimeout等）访问已移除的 DOM
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // 教师模式下不自动同步标题，由用户手动输入或上传解析时自动填充
  // useEffect(() => {
  //   const autoTitle = generateAutoTitle();
  //   setTitle(autoTitle);
  // }, [paperSettings, generateAutoTitle]);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [cats, setCats] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);

  const selectedOrg = orgs.find(o => o.name === paperSettings.organization);
  const filteredCats = selectedOrg
    ? cats.filter(c => c.orgId === selectedOrg.id)
    : [];

  const selectedCat = filteredCats.find(c => c.name === paperSettings.categoryType);
  const filteredLevels = selectedCat
    ? levels.filter(l => l.catId === selectedCat.id)
    : [];

  const years = ['', ...Array.from({ length: 21 }, (_, i) => String(2010 + i))];
  const months = [{ value: 0, label: '无' }, ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}月` }))];
  const sources = ['真题', '模拟题'];
  const natures = ['免费', '付费'];
  const paperTypes = ['练习', '考试'];

  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draft = params.get('draft');
    if (draft) setDraftId(draft);
  }, []);

  useEffect(() => {
    fetchCategories();
    if (editPaperId) {
      loadPaperForEdit(editPaperId);
    } else if (pendingPaperId) {
      loadPendingPaperData(pendingPaperId);
    } else if (draftId) {
      loadPaperDraftData(draftId);
    } else {
      // temp_imported_papers 和 temp_graphics_questions 表尚未创建，暂时跳过查询避免 404
      // loadImportedDataFromSupabase();
      // loadGraphicsQuestionFromSupabase();
      loadEditorStateFromStorage();
    }
  }, [editPaperId, pendingPaperId, draftId]);

  const loadImportedDataFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const result: any = await supabase
        .from('temp_imported_papers' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const data = result.data;
      const error = result.error;
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || (error as any).status === 404) return;
        throw error;
      }
      if (data) {
        loadImportedPaperData((data as any).paper_data);
        await supabase.from('temp_imported_papers' as any).delete().eq('id', (data as any).id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadEditorStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem('paperEditorState');
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.title) setTitle(state.title);
        if (state.description) setDescription(state.description);
        if (state.questions && state.questions.length > 0) setQuestions(state.questions);
        if (state.paperSettings) setPaperSettings(state.paperSettings);
        if (state.selectedCategory) setSelectedCategory(state.selectedCategory);
        if (state.isPendingPaper) setIsPendingPaper(state.isPendingPaper);
        if (state.pendingPaperId) setIsPendingPaper(state.pendingPaperId);
        if (state.editPaperId) setIsPendingPaper(state.editPaperId);
        localStorage.removeItem('paperEditorState');
      }

      // 从预览界面回传的图片宽度调整数据
      const widthUpdatesStr = localStorage.getItem('previewImageWidthUpdates');
      if (widthUpdatesStr) {
        const widthUpdates = JSON.parse(widthUpdatesStr);
        if (widthUpdates && Array.isArray(widthUpdates) && widthUpdates.length > 0) {
          setQuestions(prevQuestions => {
            const updatedQuestions = prevQuestions.map(q => {
              const update = widthUpdates.find(u => u.id === q.id);
              if (!update) return q;

              const updatedQ = { ...q };

              // 同步题干图片宽度
              if (update.titleImageWidths && update.titleImageWidths.length > 0) {
                updatedQ.titleImageWidths = update.titleImageWidths;
              }

              // 同步选项图片宽度
              if (update.optionImageWidths && Array.isArray(update.optionImageWidths)) {
                const updatedOptions = q.options.map(opt => {
                  const optUpdate = update.optionImageWidths.find(ow => ow.id === opt.id);
                  if (optUpdate && optUpdate.imageWidth !== undefined) {
                    return { ...opt, imageWidth: optUpdate.imageWidth };
                  }
                  return opt;
                });
                updatedQ.options = updatedOptions;
              }

              return updatedQ;
            });
            return updatedQuestions;
          });
        }
        localStorage.removeItem('previewImageWidthUpdates');
      }
    } catch (e) {
      console.error('加载编辑器状态失败:', e);
    }
  };

  const loadGraphicsQuestionFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const result: any = await supabase
        .from('temp_graphics_questions' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const data = result.data;
      const error = result.error;
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || (error as any).status === 404) return;
        throw error;
      }
      if (data) {
        addGraphicsQuestionToPaper((data as any).question_data);
        await supabase.from('temp_graphics_questions' as any).delete().eq('id', (data as any).id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadImportedPaperData = (data: any) => {
    if (data.title) {
      setTitle(data.title);
    }
    if (data.settings) {
      setPaperSettings({
        year: data.settings.year || '',
        month: data.settings.month || '',
        organization: data.settings.organization || '',
        source: data.settings.source || '',
        nature: data.settings.nature || '',
        categoryType: data.settings.categoryType || '',
        level_text: data.settings.level_text || '',
        paperType: data.settings.paperType || '',
        duration: data.settings.duration || ''
      });
    }
    if (data.questions && Array.isArray(data.questions)) {
      const importedQuestions: Question[] = data.questions.map((q: any, index: number) => ({
        id: Date.now().toString() + index,
        type: q.type || 'single_choice',
        subType: q.subType,
        questionStem: q.questionStem || q.content || q.title || '',
        placeholder: '',
        required: true,
        inputType: '文本',
        options: q.options || [],
        score: q.score || 0,
        showAnswer: false,
        titleImages: q.titleImages || [],
        titleImageWidths: q.titleImageWidths || [],
        answerImages: q.answerImages || [],
        answerImageWidths: q.answerImageWidths || [],
        codeTemplate: q.codeTemplate || '',
        codeLanguage: q.codeLanguage || '',
        allowImageUpload: q.allowImageUpload !== false,
        maxImageSize: q.maxImageSize || 5,
        allowTextInput: q.allowTextInput !== false,
        maxTextLength: q.maxTextLength || 800,
        allowFileUpload: q.allowFileUpload || false,
        maxFileSize: q.maxFileSize || 10,
        gradingMode: 'ai',
        scoringPoints: q.scoringPoints || '',
        explanation: q.explanation || '',
        answerText: q.answerText || '',
        scoringPointsImages: q.scoringPointsImages || [],
        scoringPointsImageWidths: q.scoringPointsImageWidths || [],
        referenceAnswerImages: q.referenceAnswerImages || [],
        referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
        scratchSubtype: q.scratchSubtype,
        scratchConfig: q.scratchConfig,
        referenceSb3Url: q.referenceSb3Url
      }));
      setQuestions(importedQuestions);
    }
  };

  const addGraphicsQuestionToPaper = (graphicsQ: any) => {
    const newQuestion: Question = {
      id: graphicsQ.id,
      type: 'scratch_coding',
      subType: 'scratch',
      title: '',  // 题目名称留空，由管理员手动输入
      content: graphicsQ.content || '',  // 解析的题干内容放到 content 字段（富文本编辑器）
      placeholder: '请输入代码',
      required: true,
      inputType: '文本',
      options: [],
      score: graphicsQ.score || 15,
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      imagePosition: 'below',
      answerImages: [],
      answerImageWidths: [],
      codeTemplate: '',
      codeLanguage: 'scratch',
      allowImageUpload: true,
      maxImageSize: 5,
      allowTextInput: false,
      maxTextLength: 0,
      allowFileUpload: false,
      maxFileSize: 0,
      gradingMode: 'ai',
      scoringPoints: graphicsQ.answerItems?.map((item: any) => item.name).join('、') || '',
      explanation: '',  // 不再使用 explanation 存储题干
      answerText: '',
      scoringPointsImages: [],
      scoringPointsImageWidths: [],
      referenceAnswerImages: [],
      referenceAnswerImageWidths: [],
      scratchAnswerItems: graphicsQ.answerItems?.map((item: any, idx: number) => ({
        id: item.id || String(idx + 1),
        name: item.name || `作答项${idx + 1}`,
        placeholder: item.placeholder || '请上传程序截图',
        required: true,
        maxSizeMB: 5
      })) || [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }],
      scratchSubtype: graphicsQ.scratchSubtype || 'operation',
      scratchConfig: graphicsQ.scratchConfig
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    setEditingQuestion(newQuestion.id);
    setIsEditing(true);
    // 导航到新题目位置
    const sortedQuestions = sortQuestionsByType([...questions, newQuestion]);
    const newIndex = sortedQuestions.findIndex(q => q.id === newQuestion.id);
    if (newIndex >= 0) setCurrentIndex(newIndex);
  };

  const loadPaperForEdit = async (paperId: string) => {
    try {
      const { data: paper, error } = await supabase
        .from(papersTable)
        .select('*')
        .eq('id', paperId)
        .single();

      if (error || !paper) {
        alert('加载试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from(questionsTable)
        .select('*')
        .eq('paper_id', paperId)
        .in('status', ['published', 'draft', 'hidden'])
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle(paper.title || '');
      setDescription(paper.description || '');
      setRemark(paper.remark || '');
      setPaperSettings({
        year: String(paper.year || ''),
        month: paper.month ? String(paper.month) : '',
        organization: paper.organization || '',
        source: paper.paper_type === 'real' ? '真题' : paper.paper_type === 'mock' ? '模拟题' : '',
        nature: paper.is_free !== false ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type || '',
        level_text: paper.level_text || '',
        paperType: '',
        duration: paper.duration ? String(paper.duration) : ''
      });
      setSelectedCategory(paper.category_id || '');
      setIsPendingPaper(false);

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding',
            'python_coding': 'python_coding',
            'cpp_coding': 'cpp_coding',
            'scratch_coding': 'scratch_coding',
            'coding': 'python_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            questionTitle: q.title ?? '', // 从数据库 title 字段回显到 questionTitle（题目名称）
            questionStem: q.questionStem || q.content || q.title || '', // 题干内容（数据库 content 字段）
            content: (() => { // 题干内容（数据库 content 字段，富文本）
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: q.title_images || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 100) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: q.scoring_points || '',
            explanation: q.explanation || '',
            answerText: q.reference_answer || '',
            scoringPointsImages: [],
            scoringPointsImageWidths: q.scoring_points_image_widths || [],
            referenceAnswerImages: [],
            referenceAnswerImageWidths: q.reference_answer_image_widths || [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));

        // 如果有目标题目ID，自动定位到该题目
        if (targetQuestionId) {
          setTimeout(() => {
            if (unmountedRef.current) return;
            try {
              const questionElement = document.getElementById(`question-${targetQuestionId}`);
              if (questionElement && questionElement.isConnected) {
                questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // 高亮显示该题目
                questionElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
                setTimeout(() => {
                  if (unmountedRef.current) return;
                  try {
                    if (questionElement.isConnected) {
                      questionElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
                    }
                  } catch (_) { /* 组件已卸载，静默忽略 */ }
                }, 3000);
              }
            } catch (_) { /* 组件已卸载，静默忽略 */ }
          }, 500);
        }
      } else {
        setQuestions([]);
      }
    } catch (err) {
      alert('加载试卷异常');
    }
  };

  const loadPaperDraftData = async (draftId: string) => {
    try {
      const { data: draft, error: draftError } = await supabase
        .from('paper_drafts')
        .select('*')
        .eq('id', draftId)
        .single();

      if (draftError || !draft) {
        alert('加载草稿失败');
        return;
      }

      setTitle(draft.title || '');
      setRemark(draft.remark || '');
      setIsPendingPaper(true);

      if (draft.questions && Array.isArray(draft.questions)) {
        const convertedQuestions: Question[] = draft.questions.map((q: any, index: number) => {
          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: q.type || 'single_choice',
            subType: q.subType,
            questionTitle: q.questionTitle || q.name || q.title || '', // 题目名称（草稿中可能已保存 questionTitle 字段）
            questionStem: q.questionStem || q.content || q.title || '', // 题干内容
            content: q.content || '', // 题干内容
            placeholder: q.placeholder || '请选择',
            required: true,
            inputType: q.inputType || '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || opt.label || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || 2,
            showAnswer: false,
            titleImages: q.titleImages || [],
            titleImageWidths: q.titleImageWidths || [],
            imagePosition: q.imagePosition || 'below',
            titleImageLayout: q.titleImageLayout || 'auto',
            optionLayout: q.optionLayout || 'grid',
            answerImages: q.answerImages || [],
            answerImageWidths: q.answerImageWidths || [],
            codeTemplate: q.codeTemplate || '',
            codeLanguage: q.codeLanguage || '',
            allowImageUpload: q.allowImageUpload || false,
            maxImageSize: q.maxImageSize || 5,
            allowTextInput: q.allowTextInput !== false,
            maxTextLength: q.maxTextLength || 800,
            allowFileUpload: q.allowFileUpload || false,
            maxFileSize: q.maxFileSize || 10,
            gradingMode: q.gradingMode || 'manual',
            scoringPoints: q.scoringPoints || '',
            explanation: q.explanation || '',
            answerText: q.answerText || '',
            scoringPointsImages: q.scoringPointsImages || [],
            scoringPointsImageWidths: q.scoringPointsImageWidths || [],
            referenceAnswerImages: q.referenceAnswerImages || [],
            referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
            scratchAnswerItems: q.scratchAnswerItems,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载草稿异常');
    }
  };

  const loadPendingPaperData = async (pendingId: string) => {
    try {
      const result: any = await supabase
        .from('pending_papers' as any)
        .select('*')
        .eq('id', pendingId)
        .single();
      const pendingPaper = result.data;
      const paperError = result.error;

      if (paperError || !pendingPaper) {
        alert('加载待编辑试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from(questionsTable)
        .select('*')
        .eq('pending_paper_id', pendingId)
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle((pendingPaper as any).title || '');
      setRemark((pendingPaper as any).remark || '');
      setIsPendingPaper(true);

      // 设置试卷配置信息
      setPaperSettings({
        year: String((pendingPaper as any).year || ''),
        month: (pendingPaper as any).month ? String((pendingPaper as any).month) : '',
        organization: (pendingPaper as any).organization || '',
        source: (pendingPaper as any).paper_type === 'real' ? '真题' : (pendingPaper as any).paper_type === 'mock' ? '模拟题' : '',
        nature: (pendingPaper as any).is_free === true ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode((pendingPaper as any).category_type) || (pendingPaper as any).category_type || '',
        level_text: (pendingPaper as any).level_text || '',
        paperType: '',
        duration: (pendingPaper as any).duration ? String((pendingPaper as any).duration) : ''
      });

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            questionTitle: q.title ?? '', // 从数据库 title 字段回显到 questionTitle（题目名称）
            questionStem: q.questionStem || q.content || q.title || '', // 题干内容（数据库 content 字段）
            content: (() => { // 题干内容（数据库 content 字段，富文本）
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: q.title_images || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 100) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: '',
            explanation: q.explanation || '',
            answerText: '',
            scoringPointsImages: [],
            scoringPointsImageWidths: q.scoring_points_image_widths || [],
            referenceAnswerImages: [],
            referenceAnswerImageWidths: q.reference_answer_image_widths || [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载待编辑试卷异常');
    }
  };

  const publishPendingPaper = async () => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    if (!pendingPaperId) {
      alert('待编辑试卷ID不存在');
      return;
    }

    // 教师模式下跳过级别验证
    if (!actualIsTeacherMode) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;
      const newPaperId = generateUUID();

      const autoTitle = generateAutoTitle();
      const fullTitle = title || autoTitle;

      const { data: { session } } = await supabase.auth.getSession();
      const adminId = session?.user?.id || 'admin';
      const adminName = '管理员';
      const adminUsername = 'admin';
      const currentTime = new Date().toISOString();

      const paperData = {
        id: newPaperId,
        title: fullTitle,
        description,
        year: paperSettings.year,
        month: paperSettings.month,
        organization: paperSettings.organization,
        source: paperSettings.source,
        nature: paperSettings.nature,
        total_score: totalScore,
        question_count: questions.filter(q => q.type !== 'paragraph').length,
        questions: questions.map(q => {
          const baseQuestion = {
            id: q.id,
            type: q.type,
            subType: q.subType,
            questionStem: q.questionStem || q.content || q.title,
            placeholder: q.placeholder,
            required: q.required,
            inputType: q.inputType,
            options: q.options,
            score: q.score,
            showAnswer: q.showAnswer,
            titleImages: q.titleImages,
            titleImageWidths: q.titleImageWidths,
            answerImages: q.answerImages,
            answerImageWidths: q.answerImageWidths,
            codeTemplate: q.codeTemplate,
            codeLanguage: q.codeLanguage,
            allowImageUpload: q.allowImageUpload,
            maxImageSize: q.maxImageSize,
            allowTextInput: q.allowTextInput,
            maxTextLength: q.maxTextLength,
            allowFileUpload: q.allowFileUpload,
            maxFileSize: q.maxFileSize,
            gradingMode: q.gradingMode,
            scoringPoints: q.scoringPoints,
            scoringPointsImages: q.scoringPointsImages,
            scoringPointsImageWidths: q.scoringPointsImageWidths,
            explanation: q.explanation,
            answerText: q.answerText,
            answerAnalysis: q.answerAnalysis,
            referenceAnswer: q.answerText,
            referenceAnswerImages: q.referenceAnswerImages,
            referenceAnswerImageWidths: q.referenceAnswerImageWidths,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };
          if (q.type === 'scratch_coding') {
            return {
              ...baseQuestion,
              scratchAnswerItems: q.scratchAnswerItems
            };
          }
          return baseQuestion;
        }),
        category_id: selectedCategory || null,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        is_published: true,
        is_free: paperSettings.nature === '免费',
        remark: remark || '',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        status: 'published',
        created_at: currentTime,
        updated_at: currentTime,
        created_by: adminName,
        edit_history: [{ time: currentTime, user: adminUsername, type: '创建' }],
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount
      };

      const result = await paperStorage.savePaper(paperData);
      if (!result.success) {
        alert(result.message || '保存失败');
        return;
      }

      await supabase
        .from('pending_papers' as any)
        .update({ status: 'published' })
        .eq('id', pendingPaperId as any);

      await supabase
        .from(questionsTable)
        .update({ status: 'published' })
        .eq('pending_paper_id', pendingPaperId);

      alert('发布成功！试卷已保存到试卷列表');
      navigate('/admin/papers');
    } catch (err) {
      alert('发布失败，请稍后重试');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchCategories = async () => {
    const { data: catsData } = await supabase.from('course_categories').select('*');
    const cats = (catsData || []).map((cat: any) => ({ id: String(cat.id), name: cat.name }));
    setCategories(cats);
  };

  useEffect(() => {
    const loadCategories = async () => {
      await categoryStore.init();
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });
    return unsubscribe;
  }, []);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const isAllSettingsSelected = () => {
    if (actualIsTeacherMode) {
      // 教师模式只需要有标题即可
      return !!title?.trim();
    }
    return paperSettings.year && paperSettings.organization && paperSettings.categoryType &&
           paperSettings.level_text && paperSettings.source && paperSettings.nature && paperSettings.duration;
  };

  const savePaper = async (status: 'draft' | 'published' | 'saved') => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    // 教师模式下跳过级别验证
    if (!actualIsTeacherMode) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
    const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
    const judgeCount = questions.filter(q => q.type === 'judge').length;
    const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;
    const paperId = editPaperId || generateUUID();

    const autoTitle = generateAutoTitle();
    const fullTitle = title?.trim() || autoTitle || '未命名试卷';

    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id || 'admin';
    const adminName = '管理员';
    const currentTime = new Date().toISOString();

    try {
      if (isPendingPaper && pendingPaperId && status !== 'draft') {
        await supabase.from('pending_papers' as any).delete().eq('id', pendingPaperId as any);
        await supabase.from(questionsTable).delete().eq('pending_paper_id', pendingPaperId);
      }

      if (editPaperId && status === 'draft') {
        await supabase.from(papersTable).delete().eq('id', editPaperId);
        await supabase.from(questionsTable).delete().eq('paper_id', editPaperId);
      }

      const paperData = {
        id: paperId,
        title: fullTitle,
        description,
        year: paperSettings.year ? parseInt(paperSettings.year) : null,
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: status === 'published' || status === 'saved', // saved 也设为已发布
        status: status === 'saved' ? 'hidden' : status,
        total_score: totalScore,
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        remark: remark || '',
        created_at: currentTime,
        updated_at: currentTime
      };

      // 清理 NaN 值，避免 Supabase 400 错误
      Object.keys(paperData).forEach(key => {
        if (Number.isNaN((paperData as any)[key])) {
          (paperData as any)[key] = null;
        }
      });

      if (editPaperId && status !== 'draft') {
        // update 时不能包含 id 字段，否则 Supabase 会返回 400
        const { id: _, ...updateData } = paperData;
        const { error } = await supabase.from(papersTable).update(updateData).eq('id', paperId);
        if (error) throw error;
        await supabase.from(questionsTable).delete().eq('paper_id', paperId);
      } else {
        const { error } = await supabase.from(papersTable).insert(paperData);
        if (error) throw error;
      }

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        let processedTitleImages = q.titleImages || [];
        let processedTitleImageWidths = q.titleImageWidths || [];
        // 优先使用 content（富文本题干），回退到 title（短名称）
        let processedContent = q.content || q.title || '';

        const questionData = {
          paper_id: paperId,
          title: q.questionTitle ?? q.name ?? '', // 题目名称（非客观题专用，管理用），写入数据库 title 字段
          content: processedContent,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: (q.options || []) as any,
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: q.answerText || '',
          scoring_points: q.scoringPoints || '',
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: status === 'saved' ? 'hidden' : status,
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        };

        const { error: qError } = await supabase.from(questionsTable).insert(questionData);
        if (qError) {
          
          throw qError;
        }
      }

      // 先重置 isSaving，让按钮恢复正常状态
      setIsSaving(false);
      // 使用 setTimeout 确保 UI 更新后再跳转
      setTimeout(() => {
        if (actualIsTeacherMode) {
          navigate('/teacher/papers');
        } else {
          navigate('/admin/papers');
        }
      }, 100);
    } catch (err: any) {

      setIsSaving(false);
      alert('保存失败: ' + (err?.message || '未知错误'));
    }
  };

  // 判断是否为有效的 UUID（数据库中的真实 ID）
  const isUUID = (id: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const handleSaveQuestionLayout = async (questionId: string, layoutData: {
    titleImageWidths?: number[];
    titleImageLayout?: string;
    imagePosition?: string;
    optionImageWidths?: Record<string, number>;
    optionLayout?: string;
  }) => {
    try {
      // 临时时间戳 ID（新添加但未入库的题目）跳过 PATCH，布局数据会在保存试卷时一起写入
      if (!isUUID(questionId)) {
        // 仅更新本地状态
        setQuestions(prev => prev.map(q =>
          q.id === questionId ? {
            ...q,
            titleImageWidths: layoutData.titleImageWidths,
            titleImageLayout: layoutData.titleImageLayout,
            imagePosition: layoutData.imagePosition as 'above' | 'below',
            optionImageWidths: layoutData.optionImageWidths,
            optionLayout: layoutData.optionLayout,
          } : q
        ));
        return;
      }

      const { error } = await supabase
        .from(questionsTable)
        .update({
          title_image_widths: layoutData.titleImageWidths || [],
          title_image_layout: layoutData.titleImageLayout || 'auto',
          image_position: layoutData.imagePosition || 'below',
          option_image_widths: layoutData.optionImageWidths || {},
          option_layout: layoutData.optionLayout || 'grid',
          updated_at: new Date().toISOString(),
        })
        .eq('id', questionId);

      if (error) throw error;

      // 更新本地 questions 状态
      setQuestions(prev => prev.map(q =>
        q.id === questionId ? {
          ...q,
          titleImageWidths: layoutData.titleImageWidths,
          titleImageLayout: layoutData.titleImageLayout,
          imagePosition: layoutData.imagePosition as 'above' | 'below',
          optionImageWidths: layoutData.optionImageWidths,
          optionLayout: layoutData.optionLayout,
        } : q
      ));
    } catch (err: any) {
      console.error('保存题目布局失败:', err);
      alert('保存失败: ' + (err?.message || '未知错误'));
      throw err;
    }
  };

  // 保存单个题目的名称到数据库（防抖自动保存）
  const handleSaveQuestionTitle = useCallback(async (questionId: string, titleName: string) => {
    // 清除之前的定时器
    if (titleSaveTimerRef.current) {
      clearTimeout(titleSaveTimerRef.current);
    }

    // 设置新的防抖定时器（500ms 后保存）
    titleSaveTimerRef.current = setTimeout(async () => {
      try {
        // 临时时间戳 ID（新添加但未入库的题目）跳过数据库保存
        if (!isUUID(questionId)) {
          return;
        }

        const { error } = await supabase
          .from(questionsTable)
          .update({
            title: titleName,
            updated_at: new Date().toISOString(),
          })
          .eq('id', questionId);

        if (error) {
          console.error('保存题目名称失败:', error);
        }
      } catch (err: any) {
        console.error('保存题目名称异常:', err);
      }
    }, 500);
  }, []);

  const publishPaper = () => savePaper('published');

  const saveAsDraft = async () => {
    if (isSaving) return;
    if (paperSettings.level_text) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const paperId = editPaperId || generateUUID();
    const currentTime = new Date().toISOString();

    try {
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;

      const autoTitle = generateAutoTitle();
      const finalTitle = title?.trim() || autoTitle || '未命名试卷';

      const paperData: any = {
        id: paperId,
        title: finalTitle,
        description: description || '',
        year: paperSettings.year ? parseInt(paperSettings.year) : null,
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization || '',
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text || '',
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: false,
        status: 'draft',
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        total_score: totalScore,
        updated_at: currentTime,
        remark: remark || ''
      };

      if (editPaperId) {
        const { error } = await supabase.from(papersTable).update(paperData).eq('id', paperId);
        if (error) throw error;
        await supabase.from(questionsTable).delete().eq('paper_id', paperId);
      } else {
        paperData.created_at = currentTime;
        paperData.remark = remark || '';
        const { error } = await supabase.from(papersTable).insert(paperData);
        if (error) throw error;
      }

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        let processedTitleImages = q.titleImages || [];
        let processedTitleImageWidths = q.titleImageWidths || [];
        // 优先使用 content（富文本题干），回退到 title（短名称）
        let processedContent = q.content || q.title || '';

        const questionData: any = {
          paper_id: paperId,
          title: q.questionTitle ?? q.name ?? '', // 题目名称，写入数据库 title 字段
          content: processedContent,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: q.options || [],
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: q.answerText || '',
          scoring_points: q.scoringPoints || '',
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: 'draft',
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        };

        const { error: qError } = await supabase.from(questionsTable).insert(questionData);
        if (qError) {
          
          throw qError;
        }
      }

      alert(editPaperId ? '试卷已转为草稿' : '已暂存为草稿');
      if (actualIsTeacherMode) {
        navigate('/teacher/papers');
      } else {
        navigate('/admin/pending-papers');
      }
    } catch (err: any) {
      
      alert('暂存草稿失败: ' + (err?.message || err?.error_description || '未知错误'));
    } finally {
      setIsSaving(false);
    }
  };

  const openImageViewer = (imageUrl: string, title: string, currentIndex: number = 0, totalCount: number = 1) => {
    setImageViewer({ isOpen: true, imageUrl, title, currentIndex, totalCount });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImageViewer();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const sortQuestionsByType = (qs: Question[]) => {
    const typeOrder: Record<string, number> = {
      'single_choice': 1,
      'multi_choice': 2,
      'judge': 3,
      'python_coding': 4,
      'cpp_coding': 5,
      'scratch_coding': 6,
      'subjective': 7
    };
    return [...qs].sort((a, b) => {
      const orderA = typeOrder[a.type] || 99;
      const orderB = typeOrder[b.type] || 99;
      return orderA - orderB;
    });
  };

  // 创建默认配置
  const createDefaultConfig = (subtype: ScratchQuestionSubtype): AlgorithmConfig | OperationConfig | CreativeConfig => {
    switch (subtype) {
      case 'algorithm':
        return {
          requiredBlocks: [],
          forbiddenBlocks: [],
          minBlockCount: 5,
          maxBlockCount: 100,
          requiredConcepts: [],
          efficiencyWeight: 0.3,
          logicWeight: 0.5,
          structureWeight: 0.2
        };
      case 'operation':
        return {
          requiredSprites: [],
          requiredScripts: [],
          initialState: { x: 0, y: 0, direction: 90, visible: true },
          targetState: { x: 100, y: 100, direction: 90, visible: true },
          movementBlocks: ['motion_movesteps', 'motion_goto', 'motion_glideto'],
          tolerance: 10
        };
      case 'creative':
        return {
          minSprites: 1,
          minScripts: 1,
          requiredElements: [],
          creativityWeight: 0.3,
          techniqueWeight: 0.2,
          aestheticsWeight: 0.1,
          functionalityWeight: 0.4
        };
      default:
        return {} as AlgorithmConfig;
    }
  };

  const addQuestion = (typeItem: any) => {
    if (typeItem.type === 'python_coding' || typeItem.type === 'cpp_coding') {
      const newQuestion: Question = {
        id: Date.now().toString(),
        type: typeItem.type,
        subType: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        questionTitle: '',
        title: '',
        placeholder: typeItem.placeholder,
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        allowImageUpload: false,
        maxImageSize: 0,
        allowTextInput: true,
        maxTextLength: 800,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: []
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      setEditingQuestion(newQuestion.id);
      setIsEditing(true);
      const sortedQs = sortQuestionsByType([...questions, newQuestion]);
      const newIdx = sortedQs.findIndex(q => q.id === newQuestion.id);
      if (newIdx >= 0) setCurrentIndex(newIdx);
      return;
    }

    if (typeItem.type === 'scratch_coding') {
      const subtype = typeItem.scratchSubtype || 'operation';
      const newQuestion: Question = {
        id: Date.now().toString(),
        type: 'scratch_coding',
        subType: 'scratch',
        questionTitle: '',
        name: '',
        title: '',
        content: '',
        placeholder: '请输入代码',
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: 'scratch',
        allowImageUpload: true,
        maxImageSize: 5,
        allowTextInput: false,
        maxTextLength: 0,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: [],
        scratchAnswerItems: [
          { id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }
        ],
        scratchSubtype: subtype,
        scratchConfig: createDefaultConfig(subtype)
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      setEditingQuestion(newQuestion.id);
      setIsEditing(true);
      const sortedQs = sortQuestionsByType([...questions, newQuestion]);
      const newIdx = sortedQs.findIndex(q => q.id === newQuestion.id);
      if (newIdx >= 0) setCurrentIndex(newIdx);
      return;
    }

    const getDefaultScore = (type: string) => {
      switch (type) {
        case 'single_choice': return 2;
        case 'judge': return 2;
        case 'multi_choice': return 5;
        case 'coding': return 15;
        case 'scratch_coding': return 15;
        default: return 0;
      }
    };

    const newQuestion: Question = {
      id: Date.now().toString(),
      type: typeItem.type,
      questionTitle: '',
      title: '',
      placeholder: typeItem.placeholder,
      required: true,
      inputType: '文本',
      options: typeItem.type === 'single_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: true },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : typeItem.type === 'judge' ? [
        { id: 'A', label: 'A', text: '正确', isCorrect: true },
        { id: 'B', label: 'B', text: '错误', isCorrect: false }
      ] : typeItem.type === 'multi_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : [],
      score: getDefaultScore(typeItem.type),
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      codeTemplate: '',
      codeLanguage: typeItem.type === 'python_coding' ? 'python' : typeItem.type === 'cpp_coding' ? 'cpp' : '',
      allowFileUpload: false,
      maxFileSize: 10,
      gradingMode: 'ai',
      scoringPoints: '',
      answerText: '',
      answerImages: [],
      answerImageWidths: [],
      explanation: ''
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    setEditingQuestion(newQuestion.id);
    setIsEditing(true);
    // 导航到新题目位置
    const sortedQuestions = sortQuestionsByType([...questions, newQuestion]);
    const newIndex = sortedQuestions.findIndex(q => q.id === newQuestion.id);
    if (newIndex >= 0) setCurrentIndex(newIndex);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const updateOption = (questionId: string, optionId: string, updates: Partial<Option>) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, ...updates } : opt
          )
        };
      }
      return q;
    }));
  };

  const setCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) => ({
            ...opt,
            isCorrect: opt.id === optionId
          }))
        };
      }
      return q;
    }));
  };

  const toggleCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
          )
        };
      }
      return q;
    }));
  };

  const removeQuestion = (id: string) => {
    setQuestions(sortQuestionsByType(questions.filter(q => q.id !== id)));
    if (editingQuestion === id) setEditingQuestion(null);
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setQuestions(newQuestions);
    } else if (direction === 'down' && index < questions.length - 1) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setQuestions(newQuestions);
    }
  };

  const handleDragStart = (e: React.DragEvent, question: Question, index: number) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedQuestion && questions[index].type === draggedQuestion.type) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (dragIndex === dropIndex) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const draggedQ = questions[dragIndex];
    const targetQ = questions[dropIndex];

    if (draggedQ.type !== targetQ.type) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...questions];
    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(dropIndex, 0, draggedQ);
    setQuestions(newQuestions);
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  // Quiz风格预览模式题目切换
  const handlePrevQuestion = () => {
    if (currentIndex > 0) {
      setIsEditing(false);
      setIsEditingAll(false);
      setCurrentIndex(prev => Math.max(0, prev - 1));
    }
  };

  const handleNextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setIsEditing(false);
      setIsEditingAll(false);
      setCurrentIndex(prev => Math.min(prev + 1, questions.length - 1));
    }
  };

  // 点击答题卡题号跳转
  const handleQuestionSelect = (index: number) => {
    setIsEditing(false);
    setIsEditingAll(false);
    setCurrentIndex(() => index);
  };

  // 保存宽度设置到数据库
  const handleSaveWidthSettings = async () => {
    if (!editPaperId || !hasWidthChanges) return;
    setIsSavingWidths(true);
    try {
      for (const q of questions) {
        await supabase.from(questionsTable).update({
          title_image_widths: q.titleImageWidths,
          option_image_widths: q.options?.reduce((acc: Record<string, number>, o) => {
            if (o.image && o.imageWidth) acc[o.id] = o.imageWidth;
            return acc;
          }, {}) || {},
          scoring_points_image_widths: q.scoringPointsImageWidths || [],
          reference_answer_image_widths: q.referenceAnswerImageWidths || []
        }).eq('id', q.id);
      }
      setHasWidthChanges(false);
    } catch (err) {
      console.error('保存宽度设置失败:', err);
    } finally {
      setIsSavingWidths(false);
    }
  };

  const duplicateQuestion = (question: Question) => {
    const newQuestion = {
      ...question,
      id: Date.now().toString(),
      title: question.title + ' (复制)'
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: string[] = [];
    let processedCount = 0;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newImages.push(event.target?.result as string);
        processedCount++;
        if (processedCount === files.length) {
          setUploadedImages([...uploadedImages, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const getImageUrl = (img: string | undefined): string => {
    if (!img) return '';
    if (img.startsWith('/sb-api/')) return img;
    if (img.startsWith('/storage/')) return `${getSupabaseUrl()}${img}`;
    return img;
  };

  // Scratch配置更新辅助函数
  const updateScratchConfig = (questionId: string, configUpdates: any) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.scratchConfig) return;
    
    updateQuestion(questionId, {
      scratchConfig: { ...question.scratchConfig, ...configUpdates }
    });
  };

  // 算法题配置组件
  const AlgorithmConfigPanel = ({ question }: { question: Question }) => {
    return null;
  };

  // 操作题配置组件
  const OperationConfigPanel = ({ question }: { question: Question }) => {
    const config = question.scratchConfig as OperationConfig;
    if (!config) return null;

    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
            <MousePointer className="w-4 h-4" />
            操作题判分配置
          </h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需角色（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredSprites?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredSprites: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: 小猫, 小鸟"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需脚本（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredScripts?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredScripts: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: event_whenflagclicked, motion_movesteps"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">目标X坐标</label>
                <input
                  type="number"
                  value={config.targetState?.x || 0}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    targetState: { ...config.targetState, x: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">目标Y坐标</label>
                <input
                  type="number"
                  value={config.targetState?.y || 0}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    targetState: { ...config.targetState, y: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">移动积木类型（用逗号分隔）</label>
              <input
                type="text"
                value={config.movementBlocks?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  movementBlocks: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: motion_movesteps, motion_goto, motion_glideto"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">位置容差（像素）</label>
              <input
                type="number"
                value={config.tolerance || 10}
                onChange={(e) => updateScratchConfig(question.id, { 
                  tolerance: parseInt(e.target.value) || 10 
                })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 创意题配置组件
  const CreativeConfigPanel = ({ question }: { question: Question }) => {
    const config = question.scratchConfig as CreativeConfig;
    if (!config) return null;

    return (
      <div className="space-y-4">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            创意题判分配置
          </h4>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">最少角色数</label>
                <input
                  type="number"
                  value={config.minSprites || 1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    minSprites: parseInt(e.target.value) || 1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">最少脚本数</label>
                <input
                  type="number"
                  value={config.minScripts || 1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    minScripts: parseInt(e.target.value) || 1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需元素（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredElements?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredElements: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: backdrop, sound, costume"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">功能权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.functionalityWeight || 0.4}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    functionalityWeight: parseFloat(e.target.value) || 0.4 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">创意权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.creativityWeight || 0.3}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    creativityWeight: parseFloat(e.target.value) || 0.3 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">技术权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.techniqueWeight || 0.2}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    techniqueWeight: parseFloat(e.target.value) || 0.2 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">美观权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.aestheticsWeight || 0.1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    aestheticsWeight: parseFloat(e.target.value) || 0.1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Scratch题型配置渲染
  const renderScratchConfig = (question: Question) => {
    switch (question.scratchSubtype) {
      case 'algorithm':
        return <AlgorithmConfigPanel question={question} />;
      case 'operation':
        return <OperationConfigPanel question={question} />;
      case 'creative':
        return <CreativeConfigPanel question={question} />;
      default:
        return null;
    }
  };

  // 构建答题卡数据（复用现有逻辑）
  const answerCardGroups = useMemo(() => {
    const groups: { type: string; name: string; questions: Question[] }[] = [];
    const typeOrder = ['single_choice', 'judge', 'multi_choice', 'python_coding', 'cpp_coding', 'scratch_coding'];
    const typeNamesMap: Record<string, string> = {
      single_choice: '单选题',
      judge: '判断题',
      multi_choice: '多选题',
      python_coding: 'Python编程题',
      cpp_coding: 'C++编程题',
      scratch_coding: 'Scratch编程题'
    };
    const typeLabels = ['一', '二', '三', '四', '五', '六'];
    const defaultScores: Record<string, number> = {
      single_choice: 2, judge: 2, multi_choice: 3,
      python_coding: 15, cpp_coding: 15, scratch_coding: 15
    };
    const scoreOptions = [1, 2, 3, 5, 10, 15, 20];

    typeOrder.forEach((type) => {
      const typeQuestions = questions.filter(q => q.type === type);
      if (typeQuestions.length > 0) {
        groups.push({ type, name: typeNamesMap[type], questions: typeQuestions });
      }
    });

    const otherQuestions = questions.filter(q => !typeOrder.includes(q.type));
    if (otherQuestions.length > 0) {
      groups.push({ type: 'other', name: '其他题型', questions: otherQuestions });
    }

    return { groups, typeLabels, defaultScores, scoreOptions };
  }, [questions]);

  const updateTypeScore = (type: string, score: number) => {
    setQuestions(questions.map(q => q.type === type ? { ...q, score } : q));
  };

  // 确保 currentIndex 不超出题目范围
  const safeCurrentIndex = currentIndex >= questions.length ? Math.max(0, questions.length - 1) : currentIndex;

  // 当前题目
  const currentQuestion = questions[safeCurrentIndex];

  // 获取题型标签
  const getQuestionTypeName = (type?: string, subType?: string) => {
    if (!type) return '单选题';
    const typeMap: Record<string, string> = {
      single_choice: '单选题',
      multi_choice: '多选题',
      judge: '判断题',
      subjective: '主观题',
      python_coding: 'Python编程题',
      cpp_coding: 'C++编程题',
      scratch_coding: 'Scratch编程题',
    };
    return typeMap[type] || '单选题';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部工具栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="pt-3 px-4 pb-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">试卷名称:</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="请输入试卷名称"
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 w-80 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">备注:</span>
                <div className="relative">
                  <input
                    type="text"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="请输入备注"
                    className="px-3 py-1.5 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 w-72 focus:outline-none focus:border-blue-500"
                  />
                  {remark && (
                    <button onClick={() => setRemark('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="清空备注">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowClearConfirmModal(true)} disabled={isSaving} className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                <Eraser className="w-4 h-4" />清空
              </button>
              <button
                onClick={() => setShowPreviewModal(true)}
                disabled={isSaving}
                className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4" />全卷预览
              </button>
              {/* 教师模式下隐藏【全卷编辑】和【保存并发布】按钮 */}
              {!actualIsTeacherMode && (
                <button
                  onClick={() => setIsEditingAll(true)}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Edit3 className="w-4 h-4" />全卷编辑
                </button>
              )}
              <button onClick={() => { setSaveConfirmAction('draft'); setShowSaveConfirmModal(true); }} disabled={isSaving} className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSaving ? (<><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>保存中...</>) : (<><Save className="w-4 h-4" />暂存为草稿</>)}
              </button>
              <button onClick={() => savePaper('saved')} disabled={isSaving} className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSaving ? (<><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>保存中...</>) : (<><Save className="w-4 h-4" />保存为试卷</>)}
              </button>
              {!actualIsTeacherMode && (
                <button onClick={() => { setSaveConfirmAction('published'); setShowSaveConfirmModal(true); }} disabled={isSaving} className="px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSaving ? (<><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>保存中...</>) : (<><Upload className="w-4 h-4" />保存并发布</>)}
                </button>
              )}
              {isPendingPaper && pendingPaperId && (
                <button onClick={publishPendingPaper} disabled={isSaving} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSaving ? (<><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>发布中...</>) : (<><Upload className="w-4 h-4" />一键发布</>)}
                </button>
              )}
            </div>
          </div>

          {/* 试卷设置行 - 教师模式下隐藏筛选下拉框 */}
          {!actualIsTeacherMode && (
            <div className="flex items-center gap-4 text-sm flex-wrap pb-3">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">年份:</span>
                <select value={paperSettings.year} onChange={(e) => setPaperSettings({...paperSettings, year: e.target.value})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  {years.filter(y => y).map(year => <option key={year} value={year}>{year}年</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">月份:</span>
                <select value={paperSettings.month} onChange={(e) => setPaperSettings({...paperSettings, month: e.target.value})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  <option value="0">空</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={String(month)}>{month}月</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">考评体系:</span>
                <select value={paperSettings.organization} onChange={(e) => setPaperSettings({...paperSettings, organization: e.target.value, categoryType: '', level_text: ''})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  {orgs.map(org => <option key={org.id} value={org.name}>{org.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">课程分类:</span>
                <select value={paperSettings.categoryType} onChange={(e) => setPaperSettings({...paperSettings, categoryType: e.target.value, level_text: ''})} disabled={!paperSettings.organization} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
                  <option value="">请选择</option>
                  {filteredCats.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">级别分类:</span>
                <select value={paperSettings.level_text} onChange={(e) => setPaperSettings({...paperSettings, level_text: e.target.value})} disabled={!paperSettings.categoryType} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
                  <option value="">请选择</option>
                  {filteredLevels.map(level => (<option key={level.id} value={level.name}>{level.name}</option>))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">试卷类型:</span>
                <select value={paperSettings.source} onChange={(e) => setPaperSettings({...paperSettings, source: e.target.value})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  <option value="真题">真题</option>
                  <option value="模拟题">模拟题</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">免费/付费:</span>
                <select value={paperSettings.nature} onChange={(e) => setPaperSettings({...paperSettings, nature: e.target.value})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  {natures.map(nature => <option key={nature} value={nature}>{nature}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">考试时长:</span>
                <select value={paperSettings.duration} onChange={(e) => setPaperSettings({...paperSettings, duration: e.target.value})} className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500">
                  <option value="">请选择</option>
                  <option value="30">30分钟</option>
                  <option value="60">60分钟</option>
                  <option value="90">90分钟</option>
                  <option value="120">120分钟</option>
                  <option value="150">150分钟</option>
                  <option value="180">180分钟</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 主内容区 - 左右分栏 */}
      <div className="flex h-[calc(100vh-140px)]">
        {/* 左侧栏：答题卡 + 题型列表 */}
        <div className="w-72 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          {/* 答题卡区域 - 固定高度，与右侧导航栏对齐 */}
          <div className="p-4 bg-blue-50 border-b border-blue-100 overflow-y-auto flex-shrink-0" style={{ maxHeight: 'calc(100vh - 140px - 200px)' }}>
            <h3 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
              <Calculator className="w-4 h-4" />答题卡
            </h3>
            <div className="text-xs text-gray-600 mb-3">
              已答: <span className="font-bold text-blue-600">{questions.filter(q => q.title).length}</span>/{questions.length}
              {' '}标记: <span className="font-bold text-orange-600">0</span>
              {' '}总分: <span className="font-bold text-red-600">{questions.reduce((sum, q) => sum + (q.score || 0), 0)}</span>分
            </div>
            <div className="space-y-3">
              {answerCardGroups.groups.map((group, groupIdx) => {
                const groupQuestions = group.questions;
                const currentScore = groupQuestions[0]?.score || answerCardGroups.defaultScores[group.type] || 2;
                return (
                  <div key={group.type} className="mb-3">
                    <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-2">
                      <span>{answerCardGroups.typeLabels[groupIdx]}、{group.name}（共{groupQuestions.length}题，{groupQuestions.length * currentScore}分）</span>
                      <select value={currentScore} onChange={(e) => updateTypeScore(group.type, parseInt(e.target.value))} className="text-xs px-1 py-0.5 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-500">
                        {answerCardGroups.scoreOptions.map(score => (<option key={score} value={score}>{score}分</option>))}
                      </select>
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      {groupQuestions.map((q, idx) => {
                        const globalIndex = questions.findIndex(xq => xq.id === q.id);
                        const isActive = currentIndex === globalIndex;
                        return (
                          <button
                            key={q.id}
                            onClick={() => {
                              handleQuestionSelect(globalIndex);
                            }}
                            className={`aspect-square w-full text-xs font-medium rounded border flex items-center justify-center transition-all ${
                              isActive
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                            }`}
                          >
                            {String(globalIndex + 1).padStart(2, '0')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 题型列表 */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">考试题型</h3>
            {questionTypes.map((category) => (
              <div key={category.category} className="mb-4">
                <div className="space-y-1">
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={`${item.type}-${item.scratchSubtype || 'default'}`}
                        onClick={() => addQuestion(item)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右侧主区域 - Quiz风格题目预览 */}
        <div className="flex-1 overflow-y-auto bg-gray-50 px-6 pb-6 pt-0">
          {currentQuestion ? (
            // 预览模式：所有题目统一使用 QuizStyleQuestionView（与 Quiz 答题页面一致）
            // 编辑模式：Scratch 题使用 ScratchQuestionEditor，其他题使用 QuizStyleQuestionView
            (!isEditing && !isEditingAll) ? (
              <QuizStyleQuestionView
                key={`preview-${currentQuestion.id}-${previewVersion}`}
                question={currentQuestion}
                currentIndex={safeCurrentIndex}
                totalCount={questions.length}
                examQuestions={questions}
                onPrevQuestion={handlePrevQuestion}
                onNextQuestion={handleNextQuestion}
                onExamQuestionsChange={setQuestions}
                onEditQuestion={() => setIsEditing(true)}
                hasWidthChanges={hasWidthChanges}
                onSaveWidthSettings={handleSaveWidthSettings}
                isSavingWidths={isSavingWidths}
                isEditing={false}
                onExitEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                onSaveQuestion={async (updatedQuestion) => {
                  // 确保 questionStem 与 content 同步，预览组件 QuizQuestionTitle 优先读取 questionStem
                  const syncedQuestion = {
                    ...updatedQuestion,
                    questionStem: updatedQuestion.content || updatedQuestion.questionStem || '',
                  };
                  const updatedQuestions = questions.map(q =>
                    q.id === syncedQuestion.id ? syncedQuestion : q
                  );
                  setQuestions(updatedQuestions);
                  if (isUUID(syncedQuestion.id)) {
                    try {
                      await supabase
                        .from(questionsTable)
                        .update({
                          title: syncedQuestion.questionTitle ?? syncedQuestion.name ?? '',
                          content: syncedQuestion.content || '',
                          reference_answer: syncedQuestion.referenceAnswer || null,
                          scoring_points: syncedQuestion.scoringPoints || null,
                          title_image_widths: syncedQuestion.titleImageWidths || [],
                          title_image_layout: syncedQuestion.titleImageLayout || null,
                          image_position: syncedQuestion.imagePosition || null,
                          option_layout: syncedQuestion.optionLayout || null,
                          option_image_widths: syncedQuestion.optionImageWidths || {},
                          scoring_points_image_widths: syncedQuestion.scoringPointsImageWidths || [],
                          reference_answer_image_widths: syncedQuestion.referenceAnswerImageWidths || [],
                          updated_at: new Date().toISOString(),
                        })
                        .eq('id', syncedQuestion.id);
                    } catch (err) {
                      console.error('保存题目到数据库失败:', err);
                    }
                  }
                  if (!isEditingAll) { setIsEditing(false); }
                    setPreviewVersion(v => v + 1);
                }}
                onDeleteQuestion={(questionId) => {
                  removeQuestion(questionId);
                }}
                onCancelEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                onInsertQuestion={(questionId) => {
                  setInsertTargetQuestionId(questionId);
                  setShowInsertModal(true);
                }}
                onCopyQuestion={(questionId) => {
                  setCopyTargetQuestionId(questionId);
                  setShowCopyConfirmModal(true);
                }}
                onSaveQuestionLayout={handleSaveQuestionLayout}
              />
            ) : (
              // 编辑模式：根据题型选择编辑器
              currentQuestion.type === 'scratch_coding' ? (
                <ScratchQuestionEditor
                  question={currentQuestion}
                  paperId={editPaperId || pendingPaperId || undefined}
                  isPreviewMode={false}
                  currentIndex={currentIndex}
                  examQuestions={questions}
                  totalCount={questions.length}
                  isEditing={true}
                  onEditQuestion={() => setIsEditing(true)}
                  onExitEdit={() => { setIsEditing(false); setIsEditingAll(false); setPreviewVersion(v => v + 1); }}
                  onSaveQuestion={async (updatedQuestion) => {
                    // 确保 questionStem 与 content 同步，预览组件 QuizQuestionTitle 优先读取 questionStem
                    const syncedQuestion = {
                      ...updatedQuestion,
                      questionStem: updatedQuestion.content || updatedQuestion.questionStem || '',
                    };
                    const updatedQuestions = questions.map(q =>
                      q.id === syncedQuestion.id ? syncedQuestion : q
                    );
                    setQuestions(updatedQuestions);
                    if (isUUID(syncedQuestion.id)) {
                      try {
                        await supabase
                          .from(questionsTable)
                          .update({
                            title: syncedQuestion.questionTitle ?? syncedQuestion.name ?? '',
                            content: syncedQuestion.content || '',
                            reference_answer: syncedQuestion.referenceAnswer || null,
                            scoring_points: syncedQuestion.scoringPoints || null,
                            title_image_widths: syncedQuestion.titleImageWidths || [],
                            title_image_layout: syncedQuestion.titleImageLayout || null,
                            image_position: syncedQuestion.imagePosition || null,
                            option_layout: syncedQuestion.optionLayout || null,
                            option_image_widths: syncedQuestion.optionImageWidths || {},
                            scoring_points_image_widths: syncedQuestion.scoringPointsImageWidths || [],
                            reference_answer_image_widths: syncedQuestion.referenceAnswerImageWidths || [],
                            updated_at: new Date().toISOString(),
                          })
                          .eq('id', syncedQuestion.id);
                      } catch (err) {
                        console.error('保存题目到数据库失败:', err);
                      }
                    }
                    if (!isEditingAll) { setIsEditing(false); }
                    setPreviewVersion(v => v + 1);
                  }}
                  onExamQuestionsChange={(newQuestions) => {
                    // 同步 questionStem 字段
                    const syncedQuestions = newQuestions.map(q => ({
                      ...q,
                      questionStem: q.content || q.questionStem || '',
                    }));
                    setQuestions(syncedQuestions);
                  }}
                  onCancelEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                  onPrevQuestion={() => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); }}
                  onNextQuestion={() => { if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1); }}
                  onInsertQuestion={() => { setInsertTargetQuestionId(currentQuestion.id); setShowInsertModal(true); }}
                  onCopyQuestion={() => {
                    const newQuestion = {
                      ...currentQuestion,
                      id: Date.now().toString(),
                      title: `${currentQuestion.title || '未命名题目'} (副本)`
                    };
                    const idx = questions.findIndex(q => q.id === currentQuestion.id);
                    const updatedQuestions = [
                      ...questions.slice(0, idx + 1),
                      newQuestion,
                      ...questions.slice(idx + 1)
                    ];
                    setQuestions(updatedQuestions);
                  }}
                  onDeleteQuestion={() => {
                    if (confirm('确定要删除这道题目吗?')) {
                      const updatedQuestions = questions.filter(q => q.id !== currentQuestion.id);
                      setQuestions(updatedQuestions);
                      if (currentIndex >= updatedQuestions.length) {
                        setCurrentIndex(Math.max(0, updatedQuestions.length - 1));
                      }
                    }
                  }}
                  onTitleChange={(name) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, questionTitle: name, name } : q
                    );
                    setQuestions(updatedQuestions);
                    handleSaveQuestionTitle(currentQuestion.id, name);
                  }}
                  onScoreChange={(score) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, score } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onContentChange={(content) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, content } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onReferenceAnswerChange={(referenceAnswer) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, referenceAnswer } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onScoringPointsChange={(scoringPoints) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, scoringPoints } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onScratchAnswerItemsChange={(items) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, scratchAnswerItems: items } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                />
              ) : currentQuestion.type === 'python_coding' ? (
                // Python编程题：使用 PythonQuestionEditor
                <PythonQuestionEditor
                  question={currentQuestion}
                  paperId={editPaperId || pendingPaperId || undefined}
                  isPreviewMode={false}
                  currentIndex={currentIndex}
                  examQuestions={questions}
                  totalCount={questions.length}
                  isEditing={true}
                  onEditQuestion={() => setIsEditing(true)}
                  onExitEdit={() => { setIsEditing(false); setIsEditingAll(false); setPreviewVersion(v => v + 1); }}
                  onSaveQuestion={async (updatedQuestion) => {
                    // 确保 questionStem 与 content 同步，预览组件 QuizQuestionTitle 优先读取 questionStem
                    const syncedQuestion = {
                      ...updatedQuestion,
                      questionStem: updatedQuestion.content || updatedQuestion.questionStem || '',
                    };
                    const updatedQuestions = questions.map(q =>
                      q.id === syncedQuestion.id ? syncedQuestion : q
                    );
                    setQuestions(updatedQuestions);
                    if (isUUID(syncedQuestion.id)) {
                      try {
                        await supabase
                          .from(questionsTable)
                          .update({
                            title: syncedQuestion.questionTitle ?? syncedQuestion.name ?? '',
                            content: syncedQuestion.content || '',
                            reference_answer: syncedQuestion.referenceAnswer || null,
                            scoring_points: syncedQuestion.scoringPoints || null,
                            code_template: syncedQuestion.codeTemplate || null,
                            title_image_widths: syncedQuestion.titleImageWidths || [],
                            title_image_layout: syncedQuestion.titleImageLayout || null,
                            image_position: syncedQuestion.imagePosition || null,
                            option_layout: syncedQuestion.optionLayout || null,
                            option_image_widths: syncedQuestion.optionImageWidths || {},
                            scoring_points_image_widths: syncedQuestion.scoringPointsImageWidths || [],
                            reference_answer_image_widths: syncedQuestion.referenceAnswerImageWidths || [],
                            updated_at: new Date().toISOString(),
                          })
                          .eq('id', syncedQuestion.id);
                      } catch (err) {
                        console.error('保存题目到数据库失败:', err);
                      }
                    }
                    if (!isEditingAll) { setIsEditing(false); }
                    setPreviewVersion(v => v + 1);
                  }}
                  onCancelEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                  onPrevQuestion={() => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); }}
                  onNextQuestion={() => { if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1); }}
                  onInsertQuestion={() => { setInsertTargetQuestionId(currentQuestion.id); setShowInsertModal(true); }}
                  onCopyQuestion={() => {
                    const newQuestion = {
                      ...currentQuestion,
                      id: Date.now().toString(),
                      title: `${currentQuestion.title || '未命名题目'} (副本)`
                    };
                    const idx = questions.findIndex(q => q.id === currentQuestion.id);
                    const updatedQuestions = [
                      ...questions.slice(0, idx + 1),
                      newQuestion,
                      ...questions.slice(idx + 1)
                    ];
                    setQuestions(updatedQuestions);
                  }}
                  onDeleteQuestion={() => {
                    if (confirm('确定要删除这道题目吗?')) {
                      const updatedQuestions = questions.filter(q => q.id !== currentQuestion.id);
                      setQuestions(updatedQuestions);
                      if (currentIndex >= updatedQuestions.length) {
                        setCurrentIndex(Math.max(0, updatedQuestions.length - 1));
                      }
                    }
                  }}
                  onTitleChange={(name) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, questionTitle: name, name } : q
                    );
                    setQuestions(updatedQuestions);
                    handleSaveQuestionTitle(currentQuestion.id, name);
                  }}
                  onScoreChange={(score) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, score } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onContentChange={(content) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, content } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onReferenceAnswerChange={(referenceAnswer) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, referenceAnswer } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onScoringPointsChange={(scoringPoints) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, scoringPoints } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                />
              ) : currentQuestion.type === 'cpp_coding' ? (
                // C++编程题：使用 CppQuestionEditor
                <CppQuestionEditor
                  question={currentQuestion}
                  paperId={editPaperId || pendingPaperId || undefined}
                  isPreviewMode={false}
                  currentIndex={currentIndex}
                  examQuestions={questions}
                  totalCount={questions.length}
                  isEditing={true}
                  onEditQuestion={() => setIsEditing(true)}
                  onExitEdit={() => { setIsEditing(false); setIsEditingAll(false); setPreviewVersion(v => v + 1); }}
                  onSaveQuestion={async (updatedQuestion) => {
                    // 确保 questionStem 与 content 同步，预览组件 QuizQuestionTitle 优先读取 questionStem
                    const syncedQuestion = {
                      ...updatedQuestion,
                      questionStem: updatedQuestion.content || updatedQuestion.questionStem || '',
                    };
                    const updatedQuestions = questions.map(q =>
                      q.id === syncedQuestion.id ? syncedQuestion : q
                    );
                    setQuestions(updatedQuestions);
                    if (isUUID(syncedQuestion.id)) {
                      try {
                        await supabase
                          .from(questionsTable)
                          .update({
                            title: syncedQuestion.questionTitle ?? syncedQuestion.name ?? '',
                            content: syncedQuestion.content || '',
                            reference_answer: syncedQuestion.referenceAnswer || null,
                            scoring_points: syncedQuestion.scoringPoints || null,
                            code_template: syncedQuestion.codeTemplate || null,
                            title_image_widths: syncedQuestion.titleImageWidths || [],
                            title_image_layout: syncedQuestion.titleImageLayout || null,
                            image_position: syncedQuestion.imagePosition || null,
                            option_layout: syncedQuestion.optionLayout || null,
                            option_image_widths: syncedQuestion.optionImageWidths || {},
                            scoring_points_image_widths: syncedQuestion.scoringPointsImageWidths || [],
                            reference_answer_image_widths: syncedQuestion.referenceAnswerImageWidths || [],
                            updated_at: new Date().toISOString(),
                          })
                          .eq('id', syncedQuestion.id);
                      } catch (err) {
                        console.error('保存题目到数据库失败:', err);
                      }
                    }
                    if (!isEditingAll) { setIsEditing(false); }
                    setPreviewVersion(v => v + 1);
                  }}
                  onCancelEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                  onPrevQuestion={() => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); }}
                  onNextQuestion={() => { if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1); }}
                  onInsertQuestion={() => { setInsertTargetQuestionId(currentQuestion.id); setShowInsertModal(true); }}
                  onCopyQuestion={() => {
                    const newQuestion = {
                      ...currentQuestion,
                      id: Date.now().toString(),
                      title: `${currentQuestion.title || '未命名题目'} (副本)`
                    };
                    const idx = questions.findIndex(q => q.id === currentQuestion.id);
                    const updatedQuestions = [
                      ...questions.slice(0, idx + 1),
                      newQuestion,
                      ...questions.slice(idx + 1)
                    ];
                    setQuestions(updatedQuestions);
                  }}
                  onDeleteQuestion={() => {
                    if (confirm('确定要删除这道题目吗?')) {
                      const updatedQuestions = questions.filter(q => q.id !== currentQuestion.id);
                      setQuestions(updatedQuestions);
                      if (currentIndex >= updatedQuestions.length) {
                        setCurrentIndex(Math.max(0, updatedQuestions.length - 1));
                      }
                    }
                  }}
                  onTitleChange={(name) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, questionTitle: name, name } : q
                    );
                    setQuestions(updatedQuestions);
                    handleSaveQuestionTitle(currentQuestion.id, name);
                  }}
                  onScoreChange={(score) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, score } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onContentChange={(content) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, content } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onReferenceAnswerChange={(referenceAnswer) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, referenceAnswer } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                  onScoringPointsChange={(scoringPoints) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === currentQuestion.id ? { ...q, scoringPoints } : q
                    );
                    setQuestions(updatedQuestions);
                  }}
                />
              ) : (
                // 其他题：使用 QuizStyleQuestionView 编辑模式
                <QuizStyleQuestionView
                  question={currentQuestion}
                  currentIndex={safeCurrentIndex}
                  totalCount={questions.length}
                  examQuestions={questions}
                  onPrevQuestion={handlePrevQuestion}
                  onNextQuestion={handleNextQuestion}
                  onExamQuestionsChange={setQuestions}
                  onEditQuestion={() => setIsEditing(true)}
                  hasWidthChanges={hasWidthChanges}
                  onSaveWidthSettings={handleSaveWidthSettings}
                  isSavingWidths={isSavingWidths}
                  isEditing={true}
                  onExitEdit={() => { setIsEditing(false); setIsEditingAll(false); setPreviewVersion(v => v + 1); }}
                  onSaveQuestion={async (updatedQuestion) => {
                    const updatedQuestions = questions.map(q =>
                      q.id === updatedQuestion.id ? updatedQuestion : q
                    );
                    setQuestions(updatedQuestions);
                    if (isUUID(updatedQuestion.id)) {
                      try {
                        await supabase
                          .from(questionsTable)
                          .update({
                            title: updatedQuestion.questionTitle ?? updatedQuestion.name ?? '',
                            content: updatedQuestion.content || '',
                            reference_answer: updatedQuestion.referenceAnswer || null,
                            scoring_points: updatedQuestion.scoringPoints || null,
                            title_image_widths: updatedQuestion.titleImageWidths || [],
                            title_image_layout: updatedQuestion.titleImageLayout || null,
                            image_position: updatedQuestion.imagePosition || null,
                            option_layout: updatedQuestion.optionLayout || null,
                            option_image_widths: updatedQuestion.optionImageWidths || {},
                            scoring_points_image_widths: updatedQuestion.scoringPointsImageWidths || [],
                            reference_answer_image_widths: updatedQuestion.referenceAnswerImageWidths || [],
                            updated_at: new Date().toISOString(),
                          })
                          .eq('id', updatedQuestion.id);
                      } catch (err) {
                        console.error('保存题目到数据库失败:', err);
                      }
                    }
                    if (!isEditingAll) { setIsEditing(false); }
                  }}
                  onDeleteQuestion={(questionId) => {
                    removeQuestion(questionId);
                  }}
                  onCancelEdit={() => { setIsEditing(false); setIsEditingAll(false); }}
                  onInsertQuestion={(questionId) => {
                    setInsertTargetQuestionId(questionId);
                    setShowInsertModal(true);
                  }}
                  onCopyQuestion={(questionId) => {
                    setCopyTargetQuestionId(questionId);
                    setShowCopyConfirmModal(true);
                  }}
                  onSaveQuestionLayout={handleSaveQuestionLayout}
                />
              )
            )
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              请从左侧添加题目
            </div>
          )}
        </div>
      </div>

      {imageUploadTarget && (
        <ImageUploadModal
          target={imageUploadTarget}
          onClose={() => setImageUploadTarget(null)}
          onUpload={(imageData) => {
            const { questionId, optionId } = imageUploadTarget;
            if (optionId) {
              updateOption(questionId, optionId, { image: imageData, imageWidth: 100 });
            } else {
              const newImages = [...(questions.find(q => q.id === questionId)?.titleImages || []), imageData];
              const newWidths = [...(questions.find(q => q.id === questionId)?.titleImageWidths || []), 50];
              updateQuestion(questionId, { titleImages: newImages, titleImageWidths: newWidths });
            }
            setImageUploadTarget(null);
          }}
          allowMultiple={!imageUploadTarget.optionId}
        />
      )}

      {showImageUploadDialog && imageUploadDialogConfig && (
        <ImageUploadDialog
          isOpen={showImageUploadDialog}
          onClose={() => setShowImageUploadDialog(false)}
          title={imageUploadDialogConfig.title}
          maxCount={10}
          initialImages={(() => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return [];
            switch (imageUploadDialogConfig.target) {
              case 'title': return question.titleImages || [];
              case 'answer': return question.answerImages || [];
              case 'scoringPoints': return question.scoringPointsImages || [];
              case 'referenceAnswer': return question.referenceAnswerImages || [];
              default: return [];
            }
          })()}
          onConfirm={(images) => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return;
            const widths = images.map(() => 50);
            switch (imageUploadDialogConfig.target) {
              case 'title':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  titleImages: images,
                  titleImageWidths: widths
                });
                break;
              case 'answer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  answerImages: images,
                  answerImageWidths: widths
                });
                break;
              case 'scoringPoints':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  scoringPointsImages: images,
                  scoringPointsImageWidths: widths
                });
                break;
              case 'referenceAnswer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  referenceAnswerImages: images,
                  referenceAnswerImageWidths: widths
                });
                break;
            }
            setShowImageUploadDialog(false);
          }}
        />
      )}

      {showWordImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">上传Word试卷</h3>
              <button onClick={() => setShowWordImportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">点击上传或拖拽Word文档到此处</p>
              <p className="text-xs text-gray-400">支持 .docx 格式</p>
              <input
                type="file"
                accept=".docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    alert('Word文档上传功能开发中，请选择手动创建题目');
                    setShowWordImportModal(false);
                  }
                }}
                className="hidden"
                id="word-upload"
              />
              <label
                htmlFor="word-upload"
                className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600"
              >
                选择文件
              </label>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {saveConfirmAction === 'draft' && '暂存为草稿'}
                {saveConfirmAction === 'saved' && '保存为试卷'}
                {saveConfirmAction === 'published' && '保存并发布'}
              </h3>
              <button onClick={() => setShowSaveConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              {saveConfirmAction === 'draft' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷暂存为草稿？</p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                    <p>保存后状态：草稿</p>
                    <p>可见性：仅管理员可见，不会出现在正式试卷列表</p>
                    <p>用途：方便后续继续编辑</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'saved' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷保存为发布状态？</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p>保存后状态：发布</p>
                    <p>可见性：教师可在发布作业时选择此试卷</p>
                    <p>用途：已发布的试卷可用于布置作业</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'published' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认保存并发布试卷？</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p>保存后状态：已发布</p>
                    <p>可见性：学员端可见，可正常答题</p>
                    <p>注意：发布后学员即可看到并参与考试</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={isSaving}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setShowSaveConfirmModal(false);
                  if (saveConfirmAction === 'draft') {
                    saveAsDraft();
                  } else {
                    savePaper(saveConfirmAction);
                  }
                }}
                disabled={isSaving}
                className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${
                  saveConfirmAction === 'draft' ? 'bg-orange-500 hover:bg-orange-600' :
                  saveConfirmAction === 'saved' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isSaving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    保存中...
                  </>
                ) : (
                  `确认${saveConfirmAction === 'draft' ? '暂存' : saveConfirmAction === 'saved' ? '保存' : '发布'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-red-600">清空确认</h3>
              <button onClick={() => setShowClearConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-red-600">
                  <AlertCircle className="w-6 h-6" />
                  <p className="font-medium">确定要清空所有题目吗？</p>
                </div>
                <p className="text-gray-600 text-sm">此操作将删除当前编辑中的所有题目（共 {questions.length} 道），此操作不可撤销。</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  <p>注意：试卷设置信息（年份、考评体系等）将保留不变</p>
                  <p>已保存的草稿或已发布的试卷数据不受影响</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setQuestions([]);
                  setEditingQuestion(null);
                  setShowClearConfirmModal(false);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 插入题目弹窗 */}
      {showInsertModal && insertTargetQuestionId && questions.find(q => q.id === insertTargetQuestionId) && (
        <InsertQuestionModal
          isOpen={showInsertModal}
          currentQuestion={questions.find(q => q.id === insertTargetQuestionId)!}
          currentIndex={currentIndex}
          totalQuestions={questions.length}
          onConfirm={handleInsertConfirm}
          onCancel={() => {
            setShowInsertModal(false);
            setInsertTargetQuestionId(null);
          }}
        />
      )}

      {/* 复制题目确认弹窗 */}
      <AnimatePresence>
        {showCopyConfirmModal && copyTargetQuestionId && (() => {
          const targetQuestion = questions.find(q => q.id === copyTargetQuestionId);
          if (!targetQuestion) return null;
          const targetIndex = questions.findIndex(q => q.id === copyTargetQuestionId);
          const typeName = getQuestionTypeLabel(targetQuestion.type, targetQuestion.scratchSubtype);
          return (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                className="fixed inset-0 bg-black/50 z-50"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
              >
                <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 pointer-events-auto">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">确认复制题目</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        当前第 {targetIndex + 1} 题（共 {questions.length} 题）
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <div className="px-6 py-5 space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">将复制当前题目并插入到本题之后：</p>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          第 {targetIndex + 1} 题
                        </span>
                        <span className="text-sm text-gray-700">
                          {typeName} · {targetQuestion.score}分
                        </span>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <span className="font-medium">提示：</span>
                        复制的题目将包含本题的所有内容（题干、选项、分值等），成为第 {targetIndex + 2} 题，创建后可直接编辑。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    <button
                      onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleCopyConfirm}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      确认复制
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          );
        })()}
      </AnimatePresence>

      {/* 试卷预览模态框 */}
      <AnimatePresence>
        {showPreviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <QuizAnswerMode
                paper={{
                  id: editPaperId || pendingPaperId || '',
                  title: title || '试卷预览',
                  total_score: questions.reduce((sum, q) => sum + q.score, 0),
                  category_type: '',
                  description: description || '',
                  remark: remark || '',
                  year: null,
                  month: null,
                  organization: null,
                  source: null,
                  nature: null,
                  level_text: null,
                  paper_type: null,
                  duration: 60
                }}
                examQuestions={questions}
                currentUser={null}
                paperId={editPaperId || pendingPaperId || undefined}
                isPreview={true}
                isEditorPreview={true}
                onBack={() => setShowPreviewModal(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
